// Ventilations bancaires : transactions Qonto éclatées en plusieurs contreparties.

import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { apiDelete, apiGet } from "../lib/api";
import { formatCents } from "../lib/format";
import { BankSplitDialog, type SplitLine, type SplitTransaction } from "../components/bank-split-dialog";

export const Route = createFileRoute("/splits")({
  component: SplitsPage,
});

interface SplitRow {
  transactionId: string;
  transaction: SplitTransaction | null;
  lines: SplitLine[];
  totalCents: number;
  balanced: boolean;
  applied: boolean;
}

function SplitsPage() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [lookup, setLookup] = useState("");

  const { data, isPending } = useQuery<{ splits: SplitRow[] }>({
    queryKey: ["bank-splits"],
    queryFn: () => apiGet("/api/bank-splits"),
  });
  const rows = data?.splits ?? [];

  const deleteMutation = useMutation({
    mutationFn: (transactionId: string) => apiDelete(`/api/bank-splits/${encodeURIComponent(transactionId)}`),
    onSuccess: (_r, transactionId) => {
      queryClient.invalidateQueries({ queryKey: ["bank-splits"] });
      queryClient.invalidateQueries({ queryKey: ["bank-split", transactionId] });
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ventilations bancaires</h1>
          <p className="text-sm text-gray-500 mt-1">
            Une transaction Qonto, plusieurs contreparties. Saisie depuis une écriture BQ (bouton ⑂ dans le P&amp;L ou le
            Bilan) ou par identifiant de transaction ci-contre. Appliquées à la prochaine régénération.
          </p>
        </div>
        <form
          className="flex items-center gap-2 shrink-0"
          onSubmit={(e) => {
            e.preventDefault();
            if (lookup.trim()) setEditing(lookup.trim());
          }}
        >
          <input
            type="text"
            value={lookup}
            onChange={(e) => setLookup(e.target.value)}
            placeholder="Identifiant de transaction Qonto"
            className="border border-gray-300 rounded px-2 py-1.5 text-sm w-72 font-mono"
          />
          <button type="submit" disabled={!lookup.trim()} className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-3 py-1.5 rounded font-medium disabled:opacity-50">
            Ventiler
          </button>
        </form>
      </div>

      {deleteMutation.isError && (
        <div className="bg-red-50 text-red-700 text-sm px-3 py-2 rounded border border-red-200">
          Suppression impossible : {(deleteMutation.error as Error).message}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Transaction</th>
              <th className="px-3 py-2 text-right">Montant</th>
              <th className="px-3 py-2">Contreparties</th>
              <th className="px-3 py-2">État</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.transactionId} className="border-t border-gray-100 align-top">
                <td className="px-3 py-1.5 whitespace-nowrap font-mono text-xs text-gray-500">{r.transaction?.settledAt ?? "—"}</td>
                <td className="px-3 py-1.5">
                  <div>{r.transaction?.label ?? <span className="font-mono text-xs text-gray-400">{r.transactionId}</span>}</div>
                  {r.transaction?.counterparty && <div className="text-xs text-gray-400">{r.transaction.counterparty}</div>}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">
                  {r.transaction ? (
                    <>
                      <span className="text-xs text-gray-400 mr-1">{r.transaction.side === "debit" ? "sortie" : "entrée"}</span>
                      {formatCents(r.transaction.amountCents)}
                    </>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="px-3 py-1.5">
                  <table className="text-xs">
                    <tbody>
                      {r.lines.map((l, i) => (
                        <tr key={i}>
                          <td className="pr-2 font-mono text-gray-500">{l.accountCode}</td>
                          <td className="pr-2 text-gray-700">{l.label || l.accountLabel}</td>
                          <td className="text-right tabular-nums">{formatCents(l.amountCents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </td>
                <td className="px-3 py-1.5 text-xs whitespace-nowrap">
                  {!r.transaction ? (
                    <span className="text-red-700">Transaction absente du cache Qonto</span>
                  ) : r.balanced && r.applied ? (
                    <span className="text-green-700">Appliquée</span>
                  ) : r.balanced ? (
                    <span className="text-amber-700">Soldée, à régénérer</span>
                  ) : (
                    <span className="text-red-700">Déséquilibrée ({formatCents(r.totalCents)})</span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap text-xs">
                  {r.transaction && (
                    <button onClick={() => setEditing(r.transactionId)} className="text-blue-600 hover:text-blue-800 mr-3">
                      Modifier
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (confirm("Supprimer cette ventilation ?")) deleteMutation.mutate(r.transactionId);
                    }}
                    className="text-red-600 hover:text-red-800"
                  >
                    Supprimer
                  </button>
                </td>
              </tr>
            ))}
            {!isPending && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-gray-400">
                  Aucune ventilation pour le moment.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing !== null && <BankSplitDialog transactionId={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
